import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import ResponsiveImage from 'components/base/ResponsiveImage'
import ProgressiveImage from 'components/base/ProgressiveImage'
import SectionRule from 'components/base/SectionRule/styles'
import Link from 'components/base/Link'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import RetailLounge from 'components/blocks/RetailLounge'

import LocationImage from 'assets/images/locations/medford.webp'
import LocationImage64 from 'assets/images/locations/medford-placeholder'
import HoursNotification from 'components/blocks/HoursNotification'

import { Location } from '../../../components/App/styles'

const ReatailLoungesMedford: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Insurance Agency in Medford, OR | Insurance Lounge'
      description='Insurance Lounge is your one-stop-shop for insurance coverage in Medford, Oregon. Shop Home, Auto, Life, Health & other insurance policies in seconds.'
      ogImg='/images/og-images/Medford.webp'
    />
    <PageHero theme={PageHeroTheme.medford} right={true} cta={true}>
      Insurance Agency In Medford, OR
    </PageHero>
    <Content short={true} center={true} margined={true}>
      <Title level={2}>
        Insurance Lounge <br />Medford, OR
      </Title>
      <Paragraph theme={ParagraphTheme.introLarge}>
        At Insurance Lounge in Medford, OR, we focus on technology and
        innovation to make insurance shopping easy for you. Are you in the market for insurance?
      </Paragraph>
    </Content>
    <Content center={true} margined={true}>
      <ResponsiveImage width720={true}>
        <ProgressiveImage
          src={LocationImage}
          src2x={undefined}
          placeholder={LocationImage64}
          alt='Medford Retail Store'
        />
      </ResponsiveImage>
    </Content>
    <Content center={true} margined={true}>
      <RetailLounge
        hoursWeekday='9:00am ⁠– 6:00pm'
        hoursWeekend='10:00am ⁠– 5:00pm'
        phone='541-200-2222'
      >
        <Location href='https://www.google.com/maps/place//data=!4m2!3m1!1s0x54cf7a3774f8ccab:0x3afea5712682424f' target='_blank' rel='noopener noreferrer'>
          151 Rossanley Drive
          <br />
          Suite 105
          <br />
          Medford, OR 97504
        </Location>
      </RetailLounge>
      <HoursNotification />
    </Content>
    <Content center={true} margined={true}>
      <iframe title='Map to Medford, OR Insurance Lounge' src='https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d11796.10744004029!2d-122.8830594!3d42.3419507!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x3afea5712682424f!2sInsurance%20Lounge!5e0!3m2!1sen!2sus!4v1661208433311!5m2!1sen!2sus' width='600' height='450' style={{ border:0 }} loading='lazy' referrerPolicy='no-referrer-when-downgrade' />
    </Content>
    <SectionRule />
    <Content center={true} short={true} margined={true}>
      <Title level={3}>The Insurance Lounge Difference</Title>
      <Paragraph>
        Insurance Lounge is a full-service, no-commission insurance agency providing a
        one-stop-shop for clients in the Medford, OR area. We can help you protect your most valuable
        possessions, provide for your family, and grow your investments for retirement.
      </Paragraph>
      <Title level={3}>
        Residential and Personal Insurance
      </Title>
      <Paragraph>
        The investment you’ve made in your family home deserves the security
        of a <Link to='/products/homeowners-insurance'>comprehensive homeowner’s insurance policy </Link>. We find affordable quotes from multiple providers for insurance
        to protect the structure of your home, cover liability in an unexpected event, and replace
        your personal property in the event of theft or damage. An <Link to='/products/auto-insurance'>auto insurance</Link> policy can
        cover the cost to fix or replace your vehicle in an accident. Liability covers medical bills and property damage
        for the other driver if you’re in an at-fault collision.
      </Paragraph>
      <Paragraph>
        If you or a family member is approaching age 65, our agents can help you navigate the Medicare
        landscape and find affordable premiums. Those who are still working may want to consider <Link to='/products/disability-insurance'>disability insurance</Link>, which guarantees an income if you become unable to earn a salary.
      </Paragraph>
      <Title level={3}>Commercial and Business Insurance</Title>
      <Paragraph>
        If you own a small business or manage insurance for a larger company, we can help you
        find health insurance solutions that meet the needs of your employees. We can also quote
        general business insurance that shields your company’s assets from liability.
      </Paragraph>
      <Paragraph>Shop insurance coverage options for:&nbsp;
        <Link to='/products/homeowners-insurance'>Home</Link>,&nbsp;
        <Link to='/products/auto-insurance'>Auto</Link>,&nbsp;
        <Link to='/products/life-insurance'>Life</Link>,&nbsp;
        <Link to='/products/health-insurance'>Health</Link>,&nbsp;
        <Link to='/products/medicare-insurance'>Medicare</Link>,&nbsp;
        <Link to='/products/disability-insurance'>Disability</Link>,&nbsp;
        <Link to='/products/business-insurance'>Business</Link>,&nbsp;
        <Link to='/products/investments-retirement'>Retirement</Link>.
      </Paragraph>
      <Paragraph>
        Whether you prefer to shop online, over the phone, or in-person at our comfortable,
        convenient Medford, OR location, the team at Insurance Lounge is ready to run the numbers
        and find the best home and business insurance for your needs. We also serve as a comprehensive
        health insurance agency. Start today by completing our quick and easy <Link to='/quote/new'>quote form</Link> and see the potential savings for yourself in minutes.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ReatailLoungesMedford
